import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCart, type CartResponse, deleteCartItem, subscribeToCartUpdates, updateCartItem } from "../lib/cart";
import { productPath } from "../lib/catalog";
import { formatCurrency, toSentenceCase } from "../lib/utils";

export function CartPage() {
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasItems = Boolean(cart && cart.items.length > 0);

  useEffect(() => {
    let ignore = false;

    async function loadCart() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchCart();
        if (!ignore) {
          setCart(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load your cart right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadCart();

    const unsubscribe = subscribeToCartUpdates((nextCart) => {
      if (!ignore) {
        setCart(nextCart);
      }
    });

    return () => {
      ignore = true;
      unsubscribe();
    };
  }, []);

  async function handleQuantityChange(itemId: number, quantity: number) {
    try {
      const nextCart = await updateCartItem(itemId, quantity);
      setCart(nextCart);
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "Unable to update cart.");
    }
  }

  async function handleRemove(itemId: number) {
    try {
      const nextCart = await deleteCartItem(itemId);
      setCart(nextCart);
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : "Unable to remove item.");
    }
  }

  function updateCartPreview(itemId: number, quantity: number) {
    const sanitizedQuantity = Math.max(0, quantity);

    setCart((current) =>
      current
        ? {
            ...current,
            items: current.items.map((entry) =>
              entry.id === itemId
                ? {
                    ...entry,
                    quantity: sanitizedQuantity,
                    subtotal: entry.unitPrice * sanitizedQuantity
                  }
                : entry
            ),
            summary: {
              subtotal: current.items.reduce(
                (sum, entry) =>
                  sum +
                  (entry.id === itemId
                    ? entry.unitPrice * sanitizedQuantity
                    : entry.subtotal),
                0
              ),
              total: current.items.reduce(
                (sum, entry) =>
                  sum +
                  (entry.id === itemId
                    ? entry.unitPrice * sanitizedQuantity
                    : entry.subtotal),
                0
              ),
              discount: 0,
              quantity: current.items.reduce(
                (sum, entry) =>
                  sum + (entry.id === itemId ? sanitizedQuantity : entry.quantity),
                0
              ),
              coupon: null
            }
          }
        : current
    );
  }

  return (
    <section className="mx-auto w-full max-w-[92rem] px-3 py-12 sm:px-4 lg:px-5">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="glass-panel rounded-[28px] p-6">
          <p className="eyebrow">Your cart</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">Review your items</h1>
          {loading ? (
            <p className="mt-4 text-sm text-nv-slate">Loading cart...</p>
          ) : error ? (
            <p className="mt-4 text-sm text-red-600">{error}</p>
          ) : cart && cart.items.length > 0 ? (
            <div className="mt-6 space-y-4">
              {cart.items.map((item) => (
                <div className="glass-panel overflow-hidden rounded-[28px]" key={item.id}>
                  <div className="grid gap-0 md:grid-cols-[180px_minmax(0,1fr)]">
                    <Link className="h-full min-h-[180px] overflow-hidden bg-black/5" to={productPath(item.slug, item.productId)}>
                      {item.image ? (
                        <img alt={toSentenceCase(item.name)} className="h-full w-full object-cover" src={item.image} />
                      ) : (
                        <div className="grid h-full place-items-center text-sm font-medium text-nv-slate">Image coming soon</div>
                      )}
                    </Link>
                    <div className="min-w-0 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-nv-electric">{item.category}</p>
                          <Link
                            className="mt-2 block break-words text-xl font-semibold tracking-tight hover:text-nv-electric"
                            to={productPath(item.slug, item.productId)}
                          >
                            {toSentenceCase(item.name)}
                          </Link>
                          <p className="mt-2 text-sm text-nv-slate">{formatCurrency(item.unitPrice)} each</p>
                        </div>
                        <p className="text-lg font-bold">{formatCurrency(item.subtotal)}</p>
                      </div>
                      <div className="mt-5 flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium text-nv-slate">Qty</span>
                          <div className="inline-flex items-center overflow-hidden rounded-full border border-black/10 bg-black/5">
                            <button
                              className="px-3 py-2 text-lg font-semibold text-nv-slate transition hover:bg-black/5"
                              onClick={() => void handleQuantityChange(item.id, Math.max(0, item.quantity - 1))}
                              type="button"
                            >
                              -
                            </button>
                            <input
                              className="w-16 border-x border-black/10 bg-transparent px-2 py-2 text-center text-sm font-semibold outline-none"
                              inputMode="numeric"
                              min={0}
                              onBlur={(event) => void handleQuantityChange(item.id, Math.max(0, Number(event.target.value || item.quantity)))}
                              onChange={(event) => {
                                const nextValue = Number(event.target.value);
                                if (Number.isNaN(nextValue)) {
                                  return;
                                }
                                updateCartPreview(item.id, nextValue);
                              }}
                              type="number"
                              value={item.quantity}
                            />
                            <button
                              className="px-3 py-2 text-lg font-semibold text-nv-slate transition hover:bg-black/5"
                              onClick={() => void handleQuantityChange(item.id, item.quantity + 1)}
                              type="button"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            item.inStock
                              ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border border-red-200 bg-red-50 text-red-700"
                          }`}
                        >
                          {item.inStock ? "In stock" : "Out of stock"}
                        </span>
                        <button className="text-sm font-semibold text-red-600" onClick={() => void handleRemove(item.id)} type="button">
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-[24px] border border-dashed border-black/10 p-6">
              <h2 className="text-2xl font-semibold tracking-tight">Your cart is empty</h2>
              <p className="mt-3 text-sm text-nv-slate">Add components or kits to continue to checkout.</p>
              <Link className="brand-button mt-5 inline-flex rounded-full px-5 py-3 text-sm font-semibold text-white" to="/shop">
                Continue shopping
              </Link>
            </div>
          )}
        </div>
        <aside className="glass-panel rounded-[28px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-nv-slate">Summary</p>
          <div className="mt-5 space-y-4">
            <div className="flex items-center justify-between text-sm text-nv-slate">
              <span>Items</span>
              <span>{cart?.summary.quantity || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm text-nv-slate">
              <span>Subtotal</span>
              <span>{formatCurrency(cart?.summary.subtotal || 0)}</span>
            </div>
            {(cart?.summary.discount || 0) > 0 ? (
              <div className="flex items-center justify-between text-sm text-nv-slate">
                <span>Discount</span>
                <span>-{formatCurrency(cart?.summary.discount || 0)}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between border-t border-black/10 pt-4 text-base font-semibold">
              <span>Total</span>
              <span>{formatCurrency(cart?.summary.total || 0)}</span>
            </div>
            <p className="text-sm text-nv-slate">
              Delivery and payment details will be confirmed with you through our official channels after you place the order.
            </p>
            {hasItems ? (
              <Link className="brand-button inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white" to="/shop/checkout">
                Proceed to checkout
              </Link>
            ) : (
              <button
                className="w-full cursor-not-allowed rounded-full border border-black/10 px-5 py-3 text-sm font-semibold text-nv-slate opacity-60"
                disabled
                type="button"
              >
                Proceed to checkout
              </button>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
